import { useEffect, useMemo, useState } from "react";
import { Steps } from "antd";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill-new";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Input from "../../../components/input";
import Select from "../../../components/Select";
import FileUpload from "../../../components/FileUpload";
import Button from "../../../components/button";
import CustomModal from "../../../components/CustomModal";
import "../../../styles/AddNewRFQ.css";
import { MdDeleteOutline } from "react-icons/md";
import { formats, modules } from "../../../utils/helper";
import { searchIcon } from "../../../utils/svgs";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { RFQSchema } from "../../../utils/Schema";
import { fetchUsersDropdown, fetchInventoryPartsList } from "../../../store/Slices/dropdownSlice";
import { createPartRFQ, fetchRFQsPaginated } from "../../../store/Slices/ProcurementSlice";
import { successNotify, errorNotify } from "../../../utils/toast";

const AddNewRFQ = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));

  const [currentFormStep, setCurrentFormStep] = useState(0);
  const [showPartModal, setShowPartModal] = useState(false);
  const [partSearch, setPartSearch] = useState("");
  const [selectedPartIds, setSelectedPartIds] = useState([]);
  const requiredStar = <span className="required_star">*</span>;

  const { usersDropdownData, inventoryPartsListData, loading: dropdownLoading } = useSelector((state) => state.dropdowns);

  const { createPartRFQLoading, getRFQsPaginated } = useSelector((state) => state.procurement || {});

  useEffect(() => {
    dispatch(fetchUsersDropdown());
    dispatch(fetchInventoryPartsList());
    dispatch(fetchRFQsPaginated({ page: 1, limit: 50, search_text: "", searchFilters: {} }));
  }, [dispatch]);

  const requesterOptions = useMemo(() => {
    return (usersDropdownData || []).map((user) => ({
      ...user,
      showIcon: true,
    }));
  }, [usersDropdownData]);

  const partList = useMemo(() => {
    const parts = Array.isArray(inventoryPartsListData)
      ? [...inventoryPartsListData]
      : [];

    const getSortValue = (item) => {
      const rawDate =
        item?.created_at ||
        item?.createdAt ||
        item?.updated_at ||
        item?.updatedAt;
      const dateValue = new Date(rawDate || "").getTime();
      if (Number.isFinite(dateValue) && dateValue > 0) return dateValue;

      const rawId = item?.id ?? item?.part_id ?? item?.partId ?? 0;
      const numericId = Number(String(rawId).replace(/[^\d.-]/g, ""));
      return Number.isFinite(numericId) ? numericId : 0;
    };

    parts.sort((a, b) => getSortValue(b) - getSortValue(a));

    if (!partSearch) return parts;
    const lowerSearch = partSearch.toLowerCase();
    return parts.filter(item =>
      (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
      (item.part_number && item.part_number.toLowerCase().includes(lowerSearch))
    );
  }, [inventoryPartsListData, partSearch]);

  const nextRfqLabel = useMemo(() => {
    const extractNumericValue = (value) => {
      const matches = String(value ?? "").match(/\d+/g);
      if (!matches?.length) return 0;
      const parsed = Number(matches[matches.length - 1]);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const rfqRows = Array.isArray(getRFQsPaginated?.results) ? getRFQsPaginated.results : [];
    const maxFromRows = rfqRows.reduce((acc, item) => {
      const rowValue = extractNumericValue(item?.parts_rfq_id ?? item?.id);
      return Math.max(acc, rowValue);
    }, 0);

    const totalItems = Number(getRFQsPaginated?.meta?.totalItems || 0);
    const safeTotalItems = Number.isFinite(totalItems) ? totalItems : 0;
    const nextNumber = Math.max(maxFromRows, safeTotalItems) + 1;

    return `RFQ-${nextNumber}`;
  }, [getRFQsPaginated]);

  const toggleSelect = (id) => {
    setSelectedPartIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (partList.length === 0) return;
    const allIds = partList.map((item) => item.id);
    const allSelected = allIds.every((id) => selectedPartIds.includes(id));
    if (allSelected) {
      setSelectedPartIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedPartIds((prev) => Array.from(new Set([...prev, ...allIds])));
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      validateOnMount={true}
      initialValues={{
        date_created: new Date().toISOString().split("T")[0],
        requested_by: userDetails ? { value: userDetails.id, label: userDetails.username || userDetails.name, showIcon: true } : null,
        target_delivery_date: "",
        notes: "",
        items: [],
        attachments: [],
      }}
      validationSchema={RFQSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const payload = {
            bid_count: 3, // Default as per payload example
            requested_by: values.requested_by?.value,
            target_delivery_date: values.target_delivery_date,
            notes: values.notes,
            items: values.items.map((item) => ({
              part_id: item.part_id,
              quantity: Number(item.quantity),
            })),
            files: values.attachments.map((file) => file.fileName || file.name || file || ""),
          };

          await dispatch(createPartRFQ(payload)).unwrap();
          successNotify("RFQ Created Successfully");
          navigate(-1);
        } catch (error) {
          errorNotify(error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        submitCount,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        setTouched,
        isValid,
        validateForm,
      }) => {
        const markStepOneTouched = async () => {
          await setTouched(
            {
              requested_by: true,
              target_delivery_date: true,
              date_created: true,
              attachments: true,
            },
            true
          );
        };

        const getStepOneErrors = (formErrors = {}) => ({
          requested_by: formErrors.requested_by,
          target_delivery_date: formErrors.target_delivery_date,
          date_created: formErrors.date_created,
          attachments: formErrors.attachments,
        });

        const hasStepOneErrors = (formErrors = {}) =>
          Boolean(
            getStepOneErrors(formErrors).requested_by ||
            getStepOneErrors(formErrors).target_delivery_date ||
            getStepOneErrors(formErrors).date_created ||
            getStepOneErrors(formErrors).attachments
          );

        const handleNextStep = async () => {
          await markStepOneTouched();
          const formErrors = await validateForm();

          if (!hasStepOneErrors(formErrors)) {
            setCurrentFormStep(1);
          }
        };

        const handleAddSelectedParts = () => {
          const newParts = partList
            .filter((p) => selectedPartIds.includes(p.id))
            .map((p) => ({
              part_id: p.id,
              name: p.name || p.part_number,
              quantity: "",
            }));

          // Filter out duplicates
          const existingIds = values.items.map(i => i.part_id);
          const filteredNew = newParts.filter(p => !existingIds.includes(p.part_id));

          setFieldValue("items", [...filteredNew, ...values.items]);
          setShowPartModal(false);
          setPartSearch("");
          setSelectedPartIds([]);
        };

        const updateQty = (partId, qty) => {
          const newItems = values.items.map(item =>
            item.part_id === partId ? { ...item, quantity: qty } : item
          );
          setFieldValue("items", newItems);
        };

        const removePart = (partId) => {
          const newItems = values.items.filter(item => item.part_id !== partId);
          setFieldValue("items", newItems);
        }

        const canSave = !createPartRFQLoading;
        const allVisibleSelected = partList.length > 0 && partList.every((item) => selectedPartIds.includes(item.id));
        const addedPartIds = new Set(values.items.map((item) => item.part_id));
        const modalSelectedParts = [];
        const modalUnselectedParts = [];
        partList.forEach((item) => {
          if (addedPartIds.has(item.id)) {
            modalSelectedParts.push(item);
            return;
          }
          modalUnselectedParts.push(item);
        });
        const modalPartList = [...modalSelectedParts, ...modalUnselectedParts];

        const handleSaveAttempt = async () => {
          await setTouched(
            {
              ...touched,
              requested_by: true,
              target_delivery_date: true,
              date_created: true,
              attachments: true,
              items: values.items.map(() => ({ quantity: true })),
            },
            true
          );

          const formErrors = await validateForm();

          if (!hasStepOneErrors(formErrors)) {
            setCurrentFormStep(1);
          } else {
            setCurrentFormStep(0);
          }

          handleSubmit();
        };

        return (
          <div>
            <Breadcrumbs
              values={[
                "Procurement",
                "Request for Quotations (RFQs)",
                "Add New RFQ",
              ]}
            />

            <div className="add_rfq_page">
              <div className="add_rfq_header">
                <h2>Add New RFQ</h2>
                <div className="add_rfq_actions">
                  <Button
                    shouldRow={false}
                    cancelBtnText="Cancel"
                    submitText="Save"
                    cancelBtn={() => navigate(-1)}
                    submitBtn={handleSaveAttempt}
                    disabled={!canSave}
                    loading={createPartRFQLoading}
                  />
                </div>
              </div>

              <div className="add_rfq_layout">
                <div className="rfq_info_card">
                  <div className="rfq_info_title">RFQ Information</div>
                  <div className="rfq_info_bar">
                    <h6>
                      RFQ ID: <span>{nextRfqLabel}</span>
                    </h6>
                  </div>

                  <div className="rfq_info_body">
                    <div className="rfq_steps">
                      <Steps
                        className="form_steps_vertical"
                        current={currentFormStep}
                        onChange={async (step) => {
                          if (step === 1) {
                            await handleNextStep();
                            return;
                          }

                          setCurrentFormStep(step);
                        }}
                        direction="vertical"
                        items={[
                          { title: "General Details" },
                          { title: "Parts & Quantity" },
                        ]}
                      />
                    </div>

                    <div className="rfq_step_content">
                      {currentFormStep === 0 && (
                        <>
                          <div className="rfq_form_grid">
                            <Input
                              label="Date Created"
                              type="date"
                              placeholder="YYYY"
                              required={true}
                              name="date_created"
                              value={values.date_created}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.date_created}
                              touched={touched.date_created}
                              disabled={true}
                            />
                            <Select
                              label={<>Requester {requiredStar}</>}
                              placeholder="Select Requester"
                              options={requesterOptions}
                              // value={values.requested_by}
                              value={values.requested_by}
                              onChange={(val) => setFieldValue("requested_by", val)}
                              onBlur={handleBlur}
                              error={errors.requested_by}
                              touched={touched.requested_by}
                              loading={dropdownLoading}
                              isDisabled={true}
                            />
                            <Input
                              label="Target Delivery Date"
                              type="date"
                              placeholder="YYYY"
                              required={true}
                              name="target_delivery_date"
                              value={values.target_delivery_date}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.target_delivery_date}
                              touched={touched.target_delivery_date}
                            />
                          </div>

                          <div className="rfq_attachments">
                            <p className="rfq_attach_label">Attachments {requiredStar}</p>
                            <FileUpload
                              files={values.attachments}
                              setFiles={(newFiles) => {
                                const nextFiles =
                                  typeof newFiles === "function"
                                    ? newFiles(values.attachments)
                                    : newFiles;

                                setFieldValue("attachments", nextFiles || []);
                                setFieldTouched("attachments", true, false);
                              }}
                              className="rfq_upload"
                            />
                            {touched.attachments && errors.attachments ? (
                              <div
                                className="error-message"
                                style={{
                                  color: "#ff0000de",
                                  fontSize: "12px",
                                  marginTop: "3px",
                                  marginLeft: "5px",
                                }}
                              >
                                {errors.attachments}
                              </div>
                            ) : null}
                          </div>

                          <div className="rfq_step_actions">
                            <Button
                              shouldRow={false}
                              cancelBtnText="Previous"
                              submitText="Next"
                              cancelBtn={() => { }}
                              cancelBtnDisabled={true}
                              submitBtn={handleNextStep}
                              disabled={createPartRFQLoading}
                            />
                          </div>
                        </>
                      )}

                      {currentFormStep === 1 && (
                        <>
                          <div className="rfq_parts_table">
                            <div className="rfq_parts_header">
                              <span>Part Name</span>
                              <span>Quantity Received</span>
                              <span>Action</span>
                            </div>
                            {values.items.map((part, index) => {
                              const itemError = errors.items && errors.items[index] && errors.items[index].quantity;
                              const itemTouched = touched.items && touched.items[index] && touched.items[index].quantity;
                              const showItemError = Boolean(itemError && itemTouched);
                              return (
                                <div className="rfq_parts_row" key={part.part_id}>
                                  <span>{part.name}</span>
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <input
                                      type="number"
                                      name={`items[${index}].quantity`}
                                      value={part.quantity}
                                      onChange={(e) => updateQty(part.part_id, e.target.value)}
                                      onBlur={() => setFieldTouched(`items[${index}].quantity`, true, true)}
                                      placeholder="Enter Qty"
                                      style={{ borderColor: showItemError ? 'red' : undefined }}
                                    />
                                    {showItemError && <span style={{ color: 'red', fontSize: '10px' }}>{itemError}</span>}
                                  </div>
                                  <button
                                    type="button"
                                    className="rfq_delete_btn"
                                    onClick={() => removePart(part.part_id)}
                                  >
                                    <MdDeleteOutline />
                                  </button>
                                </div>
                              )
                            })}
                            <button
                              type="button"
                              className="rfq_add_part"
                              onClick={() => {
                                setShowPartModal(true);
                              }}
                            >
                              <span className="rfq_add_part_inner">
                                <span className="rfq_add_part_icon">+</span>
                                Add Part
                              </span>
                            </button>
                          </div>
                          <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                            {typeof errors.items === 'string' && submitCount > 0 ? errors.items : null}
                          </div>

                          <div className="rfq_step_actions">
                            <Button
                              shouldRow={false}
                              cancelBtnText="Previous"
                              isShowSubmitBtn={false}
                              cancelBtn={() => setCurrentFormStep(0)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="rfq_notes_card">
                    <div className="rfq_notes_title">Notes</div>
                    <ReactQuill
                      placeholder={"Write notes here..."}
                      modules={modules}
                      theme="snow"
                      value={values.notes}
                      onChange={(val) => setFieldValue("notes", val)}
                      formats={formats}
                      className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                    />
                  </div>
                </div>
              </div>

              <CustomModal
                show={showPartModal}
                handleClose={() => {
                  setShowPartModal(false);
                  setPartSearch("");
                  setSelectedPartIds([]);
                }}
                heading="Add Part"
                size="md"
                dialogClassName="add_part_modal_dialog"
                customHeaderCss="add_part_modal_header"
              >
                <div className="add_part_modal_body add_part_modal">
                  <p className="add_part_modal_desc">Select the parts you want to request for quotation</p>

                  <div className="add_part_search">
                    {searchIcon}
                    <input
                      type="text"
                      placeholder="Search"
                      value={partSearch}
                      onChange={(e) => setPartSearch(e.target.value)}
                    />
                  </div>

                  <div className="add_part_list">
                    <button
                      type="button"
                      className="add_part_select_all"
                      onClick={toggleSelectAll}
                    >
                      <span
                        className={`add_part_checkbox add_part_checkbox_select_all ${allVisibleSelected ? "checked all-selected" : ""}`}
                      />
                      Select All
                    </button>

                    <div className="add_part_items">
                      {dropdownLoading ? (
                        <p className="add_part_state_text">Loading...</p>
                      ) : partList.length === 0 ? (
                        <p className="add_part_state_text">No parts found</p>
                      ) : (
                        modalPartList.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            className="add_part_item"
                            onClick={() => toggleSelect(item.id)}
                          >
                            <span
                              className={`add_part_checkbox ${selectedPartIds.includes(item.id) ? "checked" : ""
                                }`}
                            />
                            {item.name || item.part_number}
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="add_part_actions">
                    <Button
                      shouldRow={false}
                      cancelBtnText="Cancel"
                      submitText={`Add Parts (${selectedPartIds.length})`}
                      cancelBtn={() => {
                        setShowPartModal(false);
                        setPartSearch("");
                        setSelectedPartIds([]);
                      }}
                      submitBtn={handleAddSelectedParts}
                      isShowSubmitBtn={true}
                      disabled={selectedPartIds.length === 0}
                    />
                  </div>
                </div>
              </CustomModal>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default AddNewRFQ;
